import { useState } from "react";
import { Trans } from "react-i18next";

import { getFeatureFlags } from "@/lib/runtime-config";
import type { CollectorsDiscountData } from "@/hooks/use-apply-payment";
import {
  guideFailed,
  guideView,
  showsStartTimeout,
  showsStepDots,
  stepsWithTexts,
  WASHER_STEPS,
  type GuideTexts,
  type WasherView,
} from "@frontend-common/start-guide/start-guide-logic";
import {
  GuideKeyframes,
  guideHeader,
  guideStatus,
  GuideSteps,
  type GuidePhase,
  type GuideStep,
  type InvoiceOffer,
  RunningState,
  type StepTexts,
} from "@/features/wash-start-guide/start-guide-parts";
import { FailedState } from "@/features/wash-start-guide/start-failed-state";
import { FlowBackSlot, FlowHeader } from "@/features/payment-flow/flow-header";
import { WasherScene } from "@/features/wash-start-guide/washer-scene";

// Guides the user through starting the washer. Payment only sends the start
// impulse via the moxa — the machine starts once the door is closed and a
// program is selected on its own display (it then shows INSERT CREDIT).
// The user confirms each step with a button; meanwhile the caller keeps polling
// the moxa, and `isRunning` jumps straight to the success view from any step.
//
// `phase`: "guide" runs every step after payment. The payment flow uses
// "prepare" (door + program BEFORE payment, ends with `onPrepared`) and then
// "after-prepare" (check the program is still selected, wait for the start).

export type WashStartGuideScreenProps = {
  machineNumber: number;
  phase?: GuidePhase;
  // "prepare" only: the door is closed and the program selected
  onPrepared?: () => void;
  // "prepare" only: back from the first step, to the payment method selection
  onBack?: () => void;
  // kg, from the machine's `capacity` field; drawn on the washer's sticker
  machineCapacity: number;
  isRunning: boolean;
  // shows the account activation reminder on the success view
  isNewRegistration?: boolean;
  // apply-payment's collectors discount result, shown as a card on success
  // offer an invoice on the success view; `result` when back from the form
  invoice?: InvoiceOffer;
  collectorsDiscount?: CollectorsDiscountData;
  currencySymbol?: string;
  // success view returns to the start screen after this long
  returnAfterSeconds?: number;
  onStartAnother?: () => void;
  onFinish?: () => void;
  // payment session id (the receipt's "Blokk sorszám"), shown if it fails
  sessionId: string;
  // the machine didn't start; also set by the guide's own start timeout
  failed?: boolean;
  // no running signal within this long → failure screen
  startTimeoutSeconds?: number;
  supportPhone?: string;
};

// long enough to photograph the id or call support from the kiosk
const FAILURE_RETURN_SECONDS = 90;

const LOAD_AND_CLOSE_TEXTS: StepTexts = {
  title: (
    <Trans>
      Bepakoltad a ruhákat? Utána határozott mozdulattal csukd be az ajtót!
    </Trans>
  ),
  description: (
    <Trans>
      Ha nem sikerül finoman bezárni és nem kattan a zár, bátran csapd be
      erősebben!
    </Trans>
  ),
  confirmLabel: <Trans>Bent vannak, sikeresen bezártam az ajtót!</Trans>,
};

const TEXTS = {
  guide: {
    "load-and-close": LOAD_AND_CLOSE_TEXTS,
    program: {
      title: <Trans>Válassz programot a mosógép kijelzőjén!</Trans>,
      description: <Trans></Trans>,
    },
  },
  prepare: {
    "load-and-close": LOAD_AND_CLOSE_TEXTS,
    program: {
      title: <Trans>Válassz programot a mosógép kijelzőjén!</Trans>,
      description: <Trans></Trans>,
      confirmLabel: <Trans>Kiválasztottam a mosási programot!</Trans>,
    },
  },
  "after-prepare": {
    starting: { title: <Trans>Gép indítása…</Trans> },
    "check-program": {
      title: <Trans>Ki van választva a program?</Trans>,
      description: (
        <Trans>
          Nézd meg, hogy a gép kijelzőjén az INSERT CREDIT felirat látható-e.
        </Trans>
      ),
      sceneNote: (
        <Trans>
          Ha a program kiválasztása és a fizetés között több mint 2 perc telt
          el, a gép visszaugrik a SELECT A PROGRAM képernyőre. Ilyenkor válaszd
          ki újra a programot!
        </Trans>
      ),
    },
  },
} satisfies GuideTexts<typeof WASHER_STEPS, StepTexts>;

const PHASE_STEPS: Record<GuidePhase, GuideStep<WasherView>[]> = {
  guide: stepsWithTexts(WASHER_STEPS.guide, TEXTS.guide),
  prepare: stepsWithTexts(WASHER_STEPS.prepare, TEXTS.prepare),
  "after-prepare": stepsWithTexts(
    WASHER_STEPS["after-prepare"],
    TEXTS["after-prepare"],
  ),
};

export function WashStartGuideScreen(props: WashStartGuideScreenProps) {
  const [timedOut, setTimedOut] = useState(false);
  const failed = guideFailed({
    failed: !!props.failed,
    timedOut,
    isRunning: props.isRunning,
  });
  return (
    <FlowBackSlot>
      <div className="min-h-screen flex flex-col bg-gray-50 text-bub-dark-blue">
        <GuideKeyframes />
        <FlowHeader
          kind="washer"
          machine={{
            number: props.machineNumber,
            kind: "washer",
            capacity: props.machineCapacity,
          }}
          {...guideHeader(
            guideStatus(props.phase ?? "guide", props.isRunning, failed),
          )}
        />
        <main className="relative flex-1 flex flex-col px-12 pb-12">
          <WashStartGuideContent
            {...props}
            failed={failed}
            onStartTimeout={() => setTimedOut(true)}
          />
        </main>
      </div>
    </FlowBackSlot>
  );
}

/**
 * The guide without a header, for the payment flow. `failed` is final here:
 * the caller turns `onStartTimeout` into it.
 */
export function WashStartGuideContent({
  machineNumber,
  phase = "guide",
  onPrepared,
  onBack,
  machineCapacity,
  isRunning,
  isNewRegistration = false,
  invoice,
  collectorsDiscount,
  currencySymbol = "Ft",
  returnAfterSeconds = 15,
  onStartAnother,
  onFinish,
  sessionId,
  failed = false,
  startTimeoutSeconds = 180,
  onStartTimeout,
  supportPhone = getFeatureFlags().supportPhone,
  onPaidBeatEnd,
}: WashStartGuideScreenProps & {
  onStartTimeout: () => void;
  // the payment flow, right after payment: see `GuideSteps`
  onPaidBeatEnd?: () => void;
}) {
  // the drum spin-up plays in the guide's scene before the success view
  // coming back from the invoice form: straight to the success view
  const [spinUpDone, setSpinUpDone] = useState(!!invoice?.result);
  const view = guideView({ failed, isRunning, spinUpDone });
  const steps = PHASE_STEPS[phase];
  return view === "failed" ? (
    <FailedState
      machineNumber={machineNumber}
      sessionId={sessionId}
      supportPhone={supportPhone}
      returnAfterSeconds={FAILURE_RETURN_SECONDS}
      onFinish={onFinish}
    />
  ) : view === "running" ? (
    <RunningState
      machineNumber={machineNumber}
      wish={<Trans>Jó mosást kívánunk! Bubbles, a Te mosodád.</Trans>}
      isNewRegistration={isNewRegistration}
      invoice={invoice}
      collectorsDiscount={collectorsDiscount}
      currencySymbol={currencySymbol}
      returnAfterSeconds={returnAfterSeconds}
      onStartAnother={onStartAnother}
      onFinish={onFinish}
    />
  ) : (
    <GuideSteps
      steps={steps}
      showStartTimeout={showsStartTimeout(phase)}
      onLastConfirm={onPrepared}
      onBackFromFirst={onBack}
      machineNumber={machineNumber}
      isRunning={isRunning}
      onSpinUpEnd={() => setSpinUpDone(true)}
      startTimeoutSeconds={startTimeoutSeconds}
      onStartTimeout={onStartTimeout}
      onPaidBeatEnd={onPaidBeatEnd}
      showDots={showsStepDots("washer", phase, steps.length)}
      startTimeoutCaption={
        <Trans>
          Indítójel elküldve – a mosógép vár, hogy becsukd az ajtaját és
          kiválaszd a programot
        </Trans>
      }
      scene={(stepIndex) => (
        <WasherScene
          view={isRunning ? "running" : steps[stepIndex]!.view}
          note={steps[stepIndex]!.sceneNote}
          capacity={machineCapacity}
        />
      )}
    />
  );
}
